import { useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { removeCharacterEntities } from "../../helpers/formatters";
import { insertBoldText } from "../../helpers/insertBoldText";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { getCompanySlug } from "../../selectors";
import { USE_EXPERIMENT_COMPANY_MAIN_BENEFITS_VARIATION } from "../../store/variations/constants";
import { getVariations } from "../../store/variations/selectors";
import { RightIcon } from "../../thecreditreview/assets/icons/RightIcon";
import { CompanyContext } from "../CompanyList/CompanyContext";

const DEFAULT_MOBILE_ITEMS_TO_SHOW = 2;
const MOBILE_BREAKPOINT = 767;

export const BaseBenefitsList = ({
	expandBenefitsText = "See More Highlights",
	isExpandable = false,
	mobileItemsToShow = DEFAULT_MOBILE_ITEMS_TO_SHOW,
	...props
}) => {
	const { company, companyIndex } = useContext(CompanyContext);
	const companySlug = useSelector((state) =>
		getCompanySlug(state, { company }),
	);

	const variations = useSelector(getVariations);
	const pumpkinVariation = variations?.[companySlug]?.["fuid_946"];
	const isPumpkinVariation =
		pumpkinVariation === USE_EXPERIMENT_COMPANY_MAIN_BENEFITS_VARIATION;

	const benefitsArray = useMemo(() => {
		const fields = company?.fields || {};

		if (isPumpkinVariation && fields.alternateCompanyMainBenefits?.length) {
			return fields.alternateCompanyMainBenefits;
		}

		return fields.companyMainBenefits || [];
	}, [company?.fields, isPumpkinVariation]);

	const formattedBenefitsList = useMemo(
		() => insertBoldText(removeCharacterEntities(benefitsArray)),
		[benefitsArray],
	);

	const [isExpanded, setIsExpanded] = useState(false);
	const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT}px)`);

	const shouldShowExpandButton =
		isExpandable &&
		companyIndex !== 0 &&
		isMobile &&
		!isExpanded &&
		formattedBenefitsList.length > mobileItemsToShow;

	const handleExpandClick = (event) => {
		event.preventDefault();
		event.stopPropagation();

		setIsExpanded((prev) => !prev);
	};

	const companyBenefits = formattedBenefitsList.map((benefit, idx) => {
		const shouldHide =
			isExpandable &&
			companyIndex !== 0 &&
			isMobile &&
			!isExpanded &&
			idx >= mobileItemsToShow;

		return (
			<li
				key={idx}
				className={`${props?.listItem} ${shouldHide ? "tw:hidden" : ""}`}
			>
				{props?.icon}
				<span className="tw:text-inherit">{benefit}</span>
			</li>
		);
	});

	return (
		<div className={props?.wrapper}>
			<ul className={props?.list}>{companyBenefits}</ul>
			{shouldShowExpandButton && (
				<button
					onClick={handleExpandClick}
					className={props?.expandButton}
					data-testid="expand-benefits-button"
				>
					<span className={props?.expandBenefitsContent}>
						{expandBenefitsText}
					</span>
					<RightIcon className={props?.expandButtonIcon} />
				</button>
			)}
		</div>
	);
};

BaseBenefitsList.propTypes = {
	wrapper: PropTypes.string,
	list: PropTypes.string,
	listItem: PropTypes.string,
	icon: PropTypes.element,
	expandBenefitsText: PropTypes.string,
	isExpandable: PropTypes.bool,
	mobileItemsToShow: PropTypes.number,
	expandButton: PropTypes.string,
	expandBenefitsContent: PropTypes.string,
	expandButtonIcon: PropTypes.string,
};
